/* Lone Bandit - Kit Builder interactivity (Phase 3)
   - Card selection state with quantity stepper
   - Live progress bar (count, fill, dots, tier label, CTA)
   - Live cart panel (items, totals, discount line)
   - Add-to-cart action with discount code applied via URL param
*/

(function () {
  'use strict';

  // Bail if not on the kit builder page
  var cartEl = document.querySelector('[data-lb-bb-cart]');
  if (!cartEl) return;

  // ── Tier configuration ──────────────────────────────────────────────
  // Discount tiers in order. Each: minimum item count + discount % + label + Shopify discount code.
  var TIERS = [
    { min: 2, discount: 10, label: 'Wrangler', code: 'POSSE10' },
    { min: 3, discount: 15, label: 'Ranger',   code: 'POSSE15' },
    { min: 4, discount: 20, label: 'Outlaw',   code: 'POSSE20' }
  ];
  var MAX_VISIBLE_TIER = TIERS[TIERS.length - 1].min;

  // ── State ───────────────────────────────────────────────────────────
  // selections is keyed by variantId. Each entry: {variantId, productId, qty, name, price, image, handle}
  var selections = {};

  function totalCount() {
    var n = 0;
    for (var k in selections) n += selections[k].qty;
    return n;
  }

  function totalSubtotalCents() {
    var c = 0;
    for (var k in selections) c += selections[k].price * selections[k].qty;
    return c;
  }

  function currentTier() {
    var n = totalCount();
    var hit = null;
    for (var i = 0; i < TIERS.length; i++) {
      if (n >= TIERS[i].min) hit = TIERS[i];
    }
    return hit;
  }

  function nextTier() {
    var n = totalCount();
    for (var i = 0; i < TIERS.length; i++) {
      if (n < TIERS[i].min) return TIERS[i];
    }
    return null;
  }

  // ── Money formatting ────────────────────────────────────────────────
  function formatMoney(cents) {
    var dollars = cents / 100;
    return '$' + dollars.toFixed(2);
  }

  // ── DOM helpers ─────────────────────────────────────────────────────
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function $(sel, root) { return (root || document).querySelector(sel); }

  // ── Card selection logic ────────────────────────────────────────────
  function readCardData(card) {
    return {
      productId: card.getAttribute('data-product-id') || '',
      variantId: card.getAttribute('data-product-variant') || '',
      handle: card.getAttribute('data-product-handle') || '',
      price: parseInt(card.getAttribute('data-product-price') || '0', 10),
      name: (card.querySelector('.lb-bb-sel__name') || {}).textContent || 'Item',
      image: (card.querySelector('img') || {}).src || ''
    };
  }

  function setQty(variantId, qty, cardEl, productData) {
    if (qty <= 0) {
      delete selections[variantId];
      if (cardEl) {
        cardEl.classList.remove('is-selected');
        var qtyNumEl = cardEl.querySelector('[data-qty-num]');
        if (qtyNumEl) qtyNumEl.textContent = '1';
      }
    } else {
      if (selections[variantId]) {
        selections[variantId].qty = qty;
      } else if (productData) {
        selections[variantId] = {
          variantId: variantId,
          productId: productData.productId,
          handle: productData.handle,
          name: productData.name,
          price: productData.price,
          image: productData.image,
          qty: qty
        };
      }
      if (cardEl) {
        cardEl.classList.add('is-selected');
        var qtyEl = cardEl.querySelector('[data-qty-num]');
        if (qtyEl) qtyEl.textContent = qty;
      }
    }
    render();
  }

  function bindVariantPills(card) {
    var pills = card.querySelectorAll('[data-variant-id]');
    if (!pills.length) return;
    Array.prototype.forEach.call(pills, function (pill) {
      pill.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (pill.disabled || pill.classList.contains('is-soldout')) return;

        var newVid = pill.getAttribute('data-variant-id');
        var newPrice = parseInt(pill.getAttribute('data-variant-price') || '0', 10);
        var prevVid = card.getAttribute('data-product-variant');

        // Update active pill state
        Array.prototype.forEach.call(pills, function (p) { p.classList.remove('is-active'); });
        pill.classList.add('is-active');

        // Update card attributes to point at new variant
        card.setAttribute('data-product-variant', newVid);
        card.setAttribute('data-product-price', newPrice);

        // Update visible price if present
        var priceEl = card.querySelector('[data-lb-bb-price]');
        if (priceEl) {
          var fmt = pill.getAttribute('data-variant-price-formatted');
          if (fmt) priceEl.textContent = fmt;
        }

        // If card already has a selection under the previous variant, migrate it
        if (prevVid && prevVid !== newVid && selections[prevVid]) {
          var prev = selections[prevVid];
          delete selections[prevVid];
          selections[newVid] = {
            variantId: newVid,
            productId: prev.productId,
            handle: prev.handle,
            name: prev.name,
            price: newPrice,
            image: prev.image,
            qty: prev.qty
          };
          render();
          return;
        }

        // Auto-add: if nothing is selected yet, picking a size adds the item.
        // Reads fresh card data so the new variant id and price are used.
        if (!selections[newVid]) {
          var data = readCardData(card);
          setQty(newVid, 1, card, data);
        }
      });
    });
  }

  function bindCard(card) {
    var initialVid = card.getAttribute('data-product-variant');
    if (!initialVid) return; // skip placeholder cards with no real variant

    var minusBtn = card.querySelector('[data-qty-minus]');
    var plusBtn = card.querySelector('[data-qty-plus]');

    // Click anywhere on the card (not on stepper buttons or size pills) → toggle/add 1
    card.addEventListener('click', function (e) {
      if (e.target.closest('[data-qty-minus]') || e.target.closest('[data-qty-plus]')) return;
      if (e.target.closest('[data-variant-id]')) return;
      e.preventDefault();
      var data = readCardData(card);
      var current = selections[data.variantId];
      if (current) {
        setQty(data.variantId, 0, card);
      } else {
        setQty(data.variantId, 1, card, data);
      }
    });

    if (minusBtn) {
      minusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var data = readCardData(card);
        var current = selections[data.variantId];
        var newQty = current ? current.qty - 1 : 0;
        setQty(data.variantId, newQty, card, data);
      });
    }
    if (plusBtn) {
      plusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var data = readCardData(card);
        var current = selections[data.variantId];
        var newQty = current ? current.qty + 1 : 1;
        setQty(data.variantId, newQty, card, data);
      });
    }

    bindVariantPills(card);
  }

  // ── Render: progress bar, selector counts, cart panel ───────────────
  function render() {
    var n = totalCount();
    var tier = currentTier();
    var next = nextTier();
    var subtotal = totalSubtotalCents();
    var discountPct = tier ? tier.discount : 0;
    var discountAmt = Math.round(subtotal * (discountPct / 100));
    var total = subtotal - discountAmt;

    // Progress bar
    var progCount = $('[data-lb-bb-count]');
    if (progCount) progCount.textContent = n;

    var fillPct = Math.min(100, (n / MAX_VISIBLE_TIER) * 100);
    var progFill = $('[data-lb-bb-fill]');
    if (progFill) progFill.style.width = fillPct + '%';

    var dots = $$('[data-lb-bb-dot]');
    dots.forEach(function (dot, i) {
      var t = TIERS[i];
      if (t && n >= t.min) dot.classList.add('is-reached');
      else dot.classList.remove('is-reached');
    });

    var progTiers = $$('[data-lb-bb-tier]');
    progTiers.forEach(function (tEl) {
      var idx = parseInt(tEl.getAttribute('data-lb-bb-tier'), 10) - 1;
      var t = TIERS[idx];
      if (t && n >= t.min) tEl.classList.add('is-active');
      else tEl.classList.remove('is-active');
    });

    var progCta = $('[data-lb-bb-cta]');
    if (progCta) {
      if (next) {
        var needed = next.min - n;
        progCta.textContent = 'Add ' + needed + ' to unlock ' + next.discount + '% off';
      } else if (tier) {
        progCta.textContent = 'Maximum savings unlocked';
      } else {
        progCta.textContent = 'Add 2 to unlock 10% off';
      }
    }

    // Selector counts (per-step "X selected")
    $$('[data-lb-bb-selector]').forEach(function (selEl) {
      var stepCount = 0;
      $$('[data-lb-bb-card].is-selected', selEl).forEach(function (card) {
        var vid = card.getAttribute('data-product-variant');
        if (selections[vid]) stepCount += selections[vid].qty;
      });
      var countEl = selEl.querySelector('[data-lb-bb-sel-count]');
      if (countEl) countEl.textContent = stepCount;
    });

    // Cart panel
    var cartCount = $('[data-lb-bb-cart-count]');
    if (cartCount) cartCount.textContent = n;

    var cartFill = $('[data-lb-bb-cart-fill]');
    if (cartFill) cartFill.style.width = fillPct + '%';

    var cartMsg = $('[data-lb-bb-cart-msg]');
    if (cartMsg) {
      if (next) {
        cartMsg.textContent = 'Add ' + (next.min - n) + ' more to unlock ' + next.discount + '% off';
      } else if (tier) {
        cartMsg.textContent = 'Maximum savings (' + tier.discount + '% off) unlocked';
      } else {
        cartMsg.textContent = 'Add 2 items to unlock 10% off';
      }
    }

    var itemsEl = $('[data-lb-bb-cart-items]');
    if (itemsEl) {
      var keys = Object.keys(selections);
      if (keys.length === 0) {
        itemsEl.innerHTML = '<div class="lb-bb-cart__empty">Your pack is empty. Start building above.</div>';
      } else {
        itemsEl.innerHTML = keys.map(function (k) {
          var s = selections[k];
          var lineSubtotal = s.price * s.qty;
          var priceMarkup;
          if (tier) {
            var lineDiscounted = Math.round(lineSubtotal * (1 - tier.discount / 100));
            priceMarkup =
              '<span class="lb-bb-cart__item-was">' + formatMoney(lineSubtotal) + '</span>' +
              '<span class="lb-bb-cart__item-now">' + formatMoney(lineDiscounted) + '</span>';
          } else {
            priceMarkup = '<span class="lb-bb-cart__item-plain">' + formatMoney(lineSubtotal) + '</span>';
          }
          return '<div class="lb-bb-cart__item">' +
            '<div class="lb-bb-cart__item-img">' + (s.image ? '<img src="' + s.image + '" alt="">' : '') + '</div>' +
            '<div class="lb-bb-cart__item-info">' +
              '<div class="lb-bb-cart__item-name">' + s.name + '</div>' +
              '<div class="lb-bb-cart__item-variant">Qty ' + s.qty + '</div>' +
            '</div>' +
            '<div class="lb-bb-cart__item-meta">' + priceMarkup + '</div>' +
          '</div>';
        }).join('');
      }
    }

    // Totals
    var subEl = $('[data-lb-bb-cart-subtotal]');
    if (subEl) subEl.textContent = formatMoney(subtotal);

    var discountRow = $('[data-lb-bb-discount-row]');
    var discountLabel = $('[data-lb-bb-discount-label]');
    var discountAmtEl = $('[data-lb-bb-discount-amount]');
    if (discountRow && discountAmtEl && discountLabel) {
      if (tier) {
        discountRow.removeAttribute('hidden');
        discountLabel.textContent = 'Posse discount (' + tier.discount + '% off)';
        discountAmtEl.textContent = '-' + formatMoney(discountAmt);
      } else {
        discountRow.setAttribute('hidden', '');
      }
    }

    var totalEl = $('[data-lb-bb-cart-total]');
    if (totalEl) totalEl.textContent = formatMoney(total);

    // CTA enable/disable
    var cta = $('[data-lb-bb-cart-cta]');
    if (cta) {
      cta.disabled = n < 1;
      if (n < 1) cta.textContent = 'Add items to get started';
      else if (n < TIERS[0].min) cta.textContent = 'Continue with ' + n + ' item';
      else cta.textContent = 'Claim Your Posse Pack — ' + formatMoney(total);
    }

    // ── Sticky bottom bar (mirror of cart state) ────────────────────────
    var stickyEl = $('[data-lb-bb-sticky]');
    if (stickyEl) {
      if (n > 0) {
        stickyEl.removeAttribute('hidden');
        document.body.classList.add('lb-bb-sticky-on');
      } else {
        stickyEl.setAttribute('hidden', '');
        document.body.classList.remove('lb-bb-sticky-on');
      }
      var stCount = stickyEl.querySelector('[data-lb-bb-sticky-count]');
      if (stCount) stCount.textContent = n;
      var stTotal = stickyEl.querySelector('[data-lb-bb-sticky-total]');
      if (stTotal) stTotal.textContent = formatMoney(total);
      var stCta = stickyEl.querySelector('[data-lb-bb-sticky-cta]');
      if (stCta) stCta.disabled = n < 1;
    }
  }

  // ── Add-to-cart action ──────────────────────────────────────────────
  function buildCartAddItems() {
    return Object.keys(selections).map(function (k) {
      var s = selections[k];
      return { id: parseInt(s.variantId, 10), quantity: s.qty };
    });
  }

  function handleClaim() {
    var items = buildCartAddItems();
    if (!items.length) return;

    var tier = currentTier();
    var cta = $('[data-lb-bb-cart-cta]');
    if (cta) { cta.disabled = true; cta.textContent = 'Claiming…'; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
    .then(function (r) { return r.json(); })
    .then(function () {
      // Two-step: /discount/CODE sets the session cookie, then redirects to
      // /cart?discount=CODE which also applies via query param as a backup.
      var url = '/cart';
      if (tier && tier.code) {
        url = '/discount/' + encodeURIComponent(tier.code) + '?redirect=' + encodeURIComponent('/cart?discount=' + tier.code);
      }
      window.location.href = url;
    })
    .catch(function (err) {
      if (cta) { cta.disabled = false; cta.textContent = 'Try again'; }
      console.error('Kit builder add-to-cart failed', err);
    });
  }

  // ── Bootstrap ───────────────────────────────────────────────────────
  function init() {
    $$('[data-lb-bb-card]').forEach(bindCard);
    var cta = $('[data-lb-bb-cart-cta]');
    if (cta) cta.addEventListener('click', handleClaim);
    var stickyCta = $('[data-lb-bb-sticky-cta]');
    if (stickyCta) stickyCta.addEventListener('click', handleClaim);
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
